/*
本文の字に、文節の切れ目の <wbr> を入れる (呼び出し側が渡す区切り方で切る)。
Chrome の word-break: auto-phrase は「1 行増えたと / いう意味」「です。た / とえば」のように語の途中で割ることがあるので、
置く側は word-break: keep-all を当て、<wbr> と空白の所でだけ折れるようにする。
コード、整形済み、数と単位の塊 (md-nowrap) と末尾の塊 (md-tail) の中は触らない。字は 1 字も変えない
*/

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

const SKIP = new Set([
  "pre", "code", "kbd", "samp", "svg", "style", "script", "textarea", "math",
  "select", "option", "optgroup", "datalist", "title", "iframe", "noscript", "template",
]);
const SKIP_CLASSES = ["md-nowrap", "md-tail"];
/* 用語の印 (gl-term、ilogo-w) は語の中を割らない。ただし前後の字とのあいだの切れ目は、印をまたいで文全体で決める */
const WORD_CLASSES = ["gl-term", "ilogo-w"];
/* 印の中の「?」の釦の字は文の字に数えない */
const UNREAD = new Set(["button", "svg", "wbr", "br"]);
const BLOCK = new Set([
  "p", "li", "td", "th", "div", "ul", "ol", "table", "thead", "tbody", "tr", "blockquote",
  "h1", "h2", "h3", "h4", "h5", "h6", "dl", "dt", "dd", "figure", "figcaption", "section", "details", "summary",
]);

function classes(node: HastNode): string[] {
  const cls = node.properties?.className;
  return (Array.isArray(cls) ? cls : typeof cls === "string" ? cls.split(/\s+/) : []).map(String);
}

function skipped(node: HastNode): boolean {
  if (node.tagName && SKIP.has(node.tagName)) return true;
  return classes(node).some((c) => SKIP_CLASSES.includes(c));
}

function textOf(node: HastNode): string {
  if (node.type === "text") return node.value ?? "";
  if (node.type !== "element" || (node.tagName && (UNREAD.has(node.tagName) || BLOCK.has(node.tagName)))) return "";
  return (node.children ?? []).map(textOf).join("");
}

const JA = /[ぁ-んァ-ヶ一-龠]/;
const OPEN = /[（(「『【［\[〈《“‘]/;
/* 折れる空白。折れない空白 (NBSP) は含めない */
const SPACE = /[ \t\n\r]/;
const wbr = (): HastNode => ({ type: "element", tagName: "wbr", properties: {}, children: [] });

/*
文の字を子の並びごとにまとめて区切り、切れ目が子の境 (印の前後) に当たれば子のあいだに、
字の途中に当たればその字を割って <wbr> を置く。
印ごとに字を分けて区切ると「従来の[ウォーターフォール]型の」の印の前後に切れ目が入らず、
keep-all の下で 1 つの長い塊になって、行の幅を超えた所で語の途中から折れていた
*/
export function rehypePhraseBreaks(options: { segment: (text: string) => string[] }) {
  const walk = (node: HastNode): void => {
    const kids = node.children;
    if (!kids) return;
    const texts = kids.map((k) => (k.type === "element" && k.tagName && BLOCK.has(k.tagName) ? "" : textOf(k)));
    const whole = texts.join("");
    const cuts = new Set<number>();
    if (JA.test(whole)) {
      let at = 0;
      for (const part of options.segment(whole)) {
        at += part.length;
        // 開きかっこで行を終えない (「ルール（ / プロトコル）」)。空白の隣は空白で折れるので置かない。
        // Chrome は text-wrap: pretty の下で、折らない塊のあとの「<wbr> 空白」を、ある幅で組み直し続けて頁ごと止まった (クラウドの 11 章の頭、390 幅)
        if (!OPEN.test(whole[at - 1] ?? "") && !SPACE.test(whole[at - 1] ?? "") && !SPACE.test(whole[at] ?? "")) cuts.add(at);
      }
      if (at !== whole.length) cuts.clear();
    }
    const next: HastNode[] = [];
    let offset = 0;
    kids.forEach((child, i) => {
      const len = texts[i].length;
      const prev = next[next.length - 1];
      if (offset > 0 && len > 0 && cuts.has(offset) && prev && prev.tagName !== "wbr") next.push(wbr());
      if (child.type === "text" && child.value) {
        let from = 0;
        for (let k = 1; k < len; k++) {
          if (!cuts.has(offset + k)) continue;
          next.push({ type: "text", value: child.value.slice(from, k) }, wbr());
          from = k;
        }
        next.push(from === 0 ? child : { type: "text", value: child.value.slice(from) });
      } else {
        const word = classes(child).some((c) => WORD_CLASSES.includes(c));
        if (child.type === "element" && !skipped(child) && !word) walk(child);
        next.push(child);
      }
      offset += len;
    });
    // 開きかっこの直後のロゴの印 (.ilogo-w) は、かっこを印の中 (折らない塊) へ移す。
    // ロゴの svg は塊の頭で折れる所になり、keep-all でも「ルール（ / プロトコル）」と、かっこだけが行末に残っていた
    for (let i = 0; i + 1 < next.length; i++) {
      const t = next[i], mark = next[i + 1];
      const m = t.type === "text" ? /[（(「『【［\[〈《“‘]+$/.exec(t.value ?? "") : null;
      if (!m || !classes(mark).includes("ilogo-w") || !mark.children) continue;
      t.value = t.value!.slice(0, m.index);
      mark.children.unshift({ type: "text", value: m[0] });
    }
    node.children = next.filter((n) => n.type !== "text" || n.value !== "");
  };
  return (tree: HastNode) => walk(tree);
}
